import { Elements } from "@stripe/react-stripe-js";
import { useFormik } from "formik";
import { trim } from "lodash";
import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Input from "@/components/Input";
import Text from "@/components/Text";
import DeleteModal, { useDeleteDisClosure } from "@/components/app/DeleteModal";
import PaymentDrawer from "@/components/app/provider/billing/payment/PaymentDrawer";
import { useRoleController } from "@/components/useRoleController";
import { PROVIDER_PATHS } from "@/constants/paths";
import { useReduceDisclosure } from "@/hooks/use-reduce-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useArchiveSuperBill } from "@/queries/billings/use-archieve-super-bill";
import { useCancelSuperBill } from "@/queries/billings/use-cancel-super-bill";
import { useInvoiceMailWithPaymentLink } from "@/queries/billings/use-invoice-mail-with-payment-link";
import { useInvoiceMailWithoutPaymentLink } from "@/queries/billings/use-invoice-mail-without-payment-link";
import { getStripePromise } from "@/queries/stripe/use-stripe";
import { showToastOnApiCall } from "@/utils/utils";
import PrintSuperBill from "../PrintSuperBill";
import {
  INVOICE_ACTION,
  INVOICE_CANCEL_SUCCESS_MSG,
  InvoiceRow,
} from "../billing-constant";
import ViewPaperClaimByInvoiceIdDrawer from "../claims/ViewPaperClaimByInvoiceIdDrawer";
import { Box } from "@mui/material";

type UseInvoiceRowActionsArgs = {
  /** Re-fetches the invoice list once an action changes the invoice. */
  onRefetch: () => void;
  /**
   * Patient Chart → Financial passes its own URL here so the Billing invoice
   * screens it launches can navigate back into the chart instead of the
   * Billing area. Left undefined by the Billing module itself.
   */
  fromPath?: string;
};

/**
 * Row actions for the Billing Invoice table, shared by the Billing module and
 * the Patient Chart → Financial mirror so both behave identically.
 *
 * Pair with `getInvoiceActionOptions` from ../billing-utils, which decides
 * which of these actions a row exposes.
 */
export const useInvoiceRowActions = ({
  onRefetch,
  fromPath,
}: UseInvoiceRowActionsArgs) => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();

  const { hasUpdate: hasUpdateClaims } = useRoleController("Generate Claims");
  const { hasView: hasViewSuperbill, hasUpdate: hasUpdateSuperbill } =
    useRoleController("Generate Superbill");

  const [payDisc, voidPaymentDisc, cancelDisc, viewPaperClaimDisc] =
    useReduceDisclosure(4);
  const archiveDisclosure = useDeleteDisClosure(false);

  // Drives the printable invoice report. Held as a pair so the report can be
  // titled without looking the row up in the list again.
  const [selectedInvoice, setSelectedInvoice] = useState<{
    uuid: string;
    patientName?: string;
  } | null>(null);
  const [archive, setArchive] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const { mutateAsync: cancelSuperBill } = useCancelSuperBill();
  const { mutateAsync: archieveAPI } = useArchiveSuperBill();
  const { mutateAsync: invoiceMailWithPaymentLink } =
    useInvoiceMailWithPaymentLink();
  const { mutateAsync: invoiceMailWithoutPaymentLink } =
    useInvoiceMailWithoutPaymentLink();

  const voidFormik = useFormik({
    initialValues: { reason: "" },
    validationSchema: Yup.object().shape({
      reason: Yup.string().required("Reason is required"),
    }),
    onSubmit: () => {},
  });

  // Every invoice screen lives under the Billing route tree; the chart reaches
  // the same screens and only differs by carrying a "back to chart" origin.
  const goToBilling = (suffix: string) =>
    navigate(
      PROVIDER_PATHS.BILLING + suffix,
      fromPath ? { state: { from: fromPath } } : undefined
    );

  // Both invoice emails take the same arguments and differ only in endpoint.
  const sendInvoiceMail = (
    sendMail: typeof invoiceMailWithPaymentLink,
    row: InvoiceRow
  ) =>
    showToastOnApiCall({
      apiCall: sendMail({
        superBillId: row?.uuid ?? "",
        invoiceSentToPatient: row?.invoiceSentToPatient,
      }),
      showSnackbar,
      onSave: onRefetch,
    });

  const setArchiveStatus = (status: boolean, id?: unknown) =>
    archieveAPI({ superBillId: String(id ?? ""), status }).then((res) => {
      onRefetch();
      return res;
    });

  const handleInvoiceAction = (action: string, row: InvoiceRow) => {
    switch (action) {
      case INVOICE_ACTION.VOID_PAYMENT:
        return voidPaymentDisc.open(row?.uuid);
      case INVOICE_ACTION.ARCHIVE:
        setArchive(row?.archive ?? false);
        return archiveDisclosure.open(row?.uuid);
      case INVOICE_ACTION.PRINT:
        // Print Invoice — open the printable invoice report.
        return setSelectedInvoice({
          uuid: row?.uuid ?? "",
          patientName: row?.patientName,
        });
      case INVOICE_ACTION.VIEW:
        // View Invoice — open the invoice screen in read-only mode.
        return goToBilling(`view-invoice/${row?.patientId}/${row?.uuid}`);
      case INVOICE_ACTION.EDIT:
        return goToBilling(`update-invoice/${row?.patientId}/${row?.uuid}`);
      case INVOICE_ACTION.COLLECT_PAYMENT:
        return payDisc.open({
          uuid: null,
          patientId: row?.patientId,
          superBillId: row?.uuid,
          amount: row?.patientBalance,
          providerId: row?.renderingProviderId
            ? {
                uuid: row?.renderingProviderId,
                name: row?.renderingProviderName ?? "",
              }
            : undefined,
        });
      case INVOICE_ACTION.CREATE_CLAIM:
        return goToBilling(
          `claims/edit-claim?patientId=${row?.patientId}&superBillId=${row?.uuid ?? ""}&claimStatus=NEW`
        );
      case INVOICE_ACTION.CANCEL:
        return cancelDisc.open(row?.uuid);
      case INVOICE_ACTION.PRINT_CMS:
        return viewPaperClaimDisc.open(row?.uuid);
      case INVOICE_ACTION.SEND_WITH_LINK:
        return sendInvoiceMail(invoiceMailWithPaymentLink, row);
      case INVOICE_ACTION.SEND_WITHOUT_LINK:
        return sendInvoiceMail(invoiceMailWithoutPaymentLink, row);
    }
  };

  const invoiceActionModals: ReactNode = (
    <>
      {selectedInvoice?.uuid && (
        <PrintSuperBill
          key={selectedInvoice.uuid}
          superBillUuid={selectedInvoice.uuid}
          patientName={selectedInvoice.patientName}
          onClose={() => setSelectedInvoice(null)}
        />
      )}

      {viewPaperClaimDisc.opened && (
        <ViewPaperClaimByInvoiceIdDrawer disclosureHost={viewPaperClaimDisc} />
      )}

      <DeleteModal
        disableOnSaveSnackbar
        w="450px"
        open={voidPaymentDisc.opened}
        onClose={voidPaymentDisc.close}
        id={voidPaymentDisc.data}
        disabledConfirmation={!voidFormik.isValid}
        cancelButtonText="Cancel"
        confirmButtonText="Confirm Void"
        onConfirmation={async () => {
          voidFormik.submitForm();
          return new Error();
        }}
        content={
          "Are you sure You want to mark this payment as void? This action will reset the balance to zero."
        }
        input={
          <Box width="100%">
            <Input
              required
              error={voidFormik.errors.reason}
              label="Reason for voiding"
              textFieldProps={{
                name: "reason",
                onBlur: voidFormik.handleBlur,
                multiline: true,
                rows: 2,
                placeholder: "Enter a Reason...",
                value: voidFormik.values.reason,
                onChange: (e) =>
                  voidFormik.setFieldValue("reason", e.target.value),
              }}
            />
          </Box>
        }
      />

      <DeleteModal
        w="450px"
        open={cancelDisc.opened}
        onClose={() => {
          cancelDisc.close();
          setCancelReason("");
        }}
        id={cancelDisc.data}
        disabledConfirmation={!cancelReason}
        cancelButtonText="Close"
        confirmButtonText="Cancel Invoice"
        // The backend success message spells it "cancelled"; the toast is
        // raised here instead so it matches the "Canceled" status chip.
        disableOnSaveSnackbar
        onConfirmation={async (id) => {
          if (!cancelReason) return;
          return cancelSuperBill({
            superBillId: id as string,
            cancelReason: trim(cancelReason),
          }).then((res) => {
            showSnackbar({
              type: "success",
              message: INVOICE_CANCEL_SUCCESS_MSG,
            });
            cancelDisc.close();
            setCancelReason("");
            onRefetch();
            return res;
          });
        }}
        content={"Do you want to cancel this invoice?"}
        input={
          <Box width="100%">
            <Input
              required
              label="Reason for Cancellation"
              textFieldProps={{
                name: "reason",
                multiline: true,
                rows: 2,
                placeholder: "Enter a Reason...",
                value: cancelReason,
                onChange: (e) => setCancelReason(e.target.value),
              }}
            />
            {!cancelReason?.trim() && (
              <Text fontSize={"12px"} sx={{ color: "#1A1A1A99", mt: "4px" }}>
                Please add reason for Cancellation
              </Text>
            )}
          </Box>
        }
      />

      <DeleteModal
        w="380px"
        open={archiveDisclosure.opened}
        onClose={archiveDisclosure.close}
        id={archiveDisclosure.deleteId}
        onConfirmation={(id) => setArchiveStatus(!archive, id)}
        content={`You want to ${archive ? "unarchive" : "archive"} this Super Bill`}
      />

      <Elements stripe={getStripePromise()}>
        <PaymentDrawer
          disclosureHost={payDisc}
          amount={payDisc?.data?.amount}
          onSuccess={onRefetch}
          shouldPassPatientId={true}
        />
      </Elements>
    </>
  );

  return {
    handleInvoiceAction,
    invoiceActionModals,
    /** Permission map for `roleFilter`, keyed by action value. */
    invoiceActionPermissions: {
      [INVOICE_ACTION.CREATE_CLAIM]: hasUpdateClaims,
      [INVOICE_ACTION.EDIT]: hasUpdateSuperbill,
      [INVOICE_ACTION.VIEW]: hasViewSuperbill,
    },
  };
};
